import { channel, seo } from '../data/channel';
import { longForm } from './videoLists';
import { videoThumbnail, videoUrl } from './video';
import type { Video } from '../types/video';

/** Sets title, meta tags, Open Graph and JSON-LD structured data at startup */
export function applySeo(): void {
  if (typeof document === 'undefined') return;
  const url = window.location.origin + window.location.pathname;

  document.documentElement.lang = 'uk';
  document.title = seo.title;

  setMeta('name', 'description', seo.description);
  setMeta('name', 'keywords', seo.keywords);
  setMeta('name', 'author', channel.author);
  setMeta('name', 'robots', 'index, follow, max-image-preview:large');
  setMeta('name', 'theme-color', seo.themeColor);

  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:locale', 'uk_UA');
  setMeta('property', 'og:site_name', channel.name);
  setMeta('property', 'og:title', seo.title);
  setMeta('property', 'og:description', seo.description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', seo.image);

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', seo.title);
  setMeta('name', 'twitter:description', seo.description);
  setMeta('name', 'twitter:image', seo.image);

  setLink('canonical', url);

  setJsonLd('ld-site', {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${url}#website`,
        url,
        name: channel.name,
        description: seo.description,
        inLanguage: 'uk',
        publisher: { '@id': `${url}#author` },
      },
      {
        '@type': 'Person',
        '@id': `${url}#author`,
        name: channel.author,
        description: channel.description,
        url,
        sameAs: [channel.url],
        email: `mailto:${channel.email}`,
        knowsAbout: ['Походи в гори', 'Карпати', 'Скелелазіння', 'Сноубординг'],
        nationality: { '@type': 'Country', name: 'Україна' },
      },
    ],
  });

  setJsonLd('ld-videos', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Відео каналу ${channel.name}`,
    itemListElement: longForm.map((video, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: videoObject(video),
    })),
  });
}

function videoObject(video: Video) {
  return {
    '@type': 'VideoObject',
    name: video.title,
    description:
      video.description ??
      `${video.title}. Відео-звіт з каналу ${channel.name}${video.place ? ` — ${video.place}, ${video.country ?? ''}` : ''}.`,
    thumbnailUrl: [videoThumbnail(video, 'hq')],
    uploadDate: video.publishedAt,
    duration: isoDuration(video.duration),
    contentUrl: videoUrl(video),
    embedUrl: `https://www.youtube.com/embed/${video.id}`,
    inLanguage: 'uk',
    author: { '@type': 'Person', name: channel.author },
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: video.views,
    },
    ...(video.place && {
      contentLocation: { '@type': 'Place', name: `${video.place}${video.country ? `, ${video.country}` : ''}` },
    }),
  };
}

function isoDuration(duration: string): string {
  const parts = duration.split(':').map(Number).reverse();
  const [s = 0, m = 0, h = 0] = parts;
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${s}S`;
}

function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd(id: string, data: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
